import React from 'react';
import './EntryOverlay.css';
import { FallingTiles } from './FallingTiles';
import { EntrySection } from './EntrySection';

interface PersonalEntryProps {
  onReturnToMap: () => void;
  entryPosition?: { x: number; y: number };
  onNext?: () => void;
  nextLabel?: string;
}

export const PersonalEntry: React.FC<PersonalEntryProps> = ({
  onReturnToMap,
  onNext,
  nextLabel,
}: PersonalEntryProps) => {
  return (
    <div className="entry-overlay has-falling-tiles">
      <FallingTiles />
      <div className="entry-card accent-personal">
        <div className="entry-header">
          <h2 className="entry-title">About Me</h2>
        </div>

        <div className="entry-body">
          <div className="entry-description">
            <p>
              Hey — I'm Wes. A lifelong builder whose early love of video games
              led me to ask how things work and eventually build software for
              people. That curiosity still drives my engineering: I enjoy
              exploring new tech, shipping real products, and helping teams
              solve meaningful problems.
            </p>
          </div>

          <EntrySection title="My Origin Story">
            <div className="entry-description">
              <p>
                My curiosity began with console hacking and tinkering{' '}
                (<a
                  href="https://en.wikipedia.org/wiki/JTAG"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--accent)', textDecoration: 'underline' }}
                >
                  JTAG
                </a>
                /
                <a
                  href="https://en.wikipedia.org/wiki/Reset_glitch_hack"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--accent)', textDecoration: 'underline' }}
                >
                  RGH
                </a>
                ). That hands-on experimentation led to building small tools and
                games in high school and set the foundation for a career of
                learning-by-doing.
              </p>
            </div>
          </EntrySection>

          <EntrySection title="University & Beyond">
            <div className="entry-description">
              <p>
                Studied Computer Science at Clemson and applied curiosity to
                internships and research: embedded R&D and web development
                co-ops at <a
                  href="https://na.itron.com/products/socketap"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--accent)', textDecoration: 'underline' }}
                >
                  Itron
                </a>, NSF-backed CNN/Deep Learning work on{' '}
                <a
                  href="https://dl.acm.org/doi/10.1145/3041008.3041013"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--accent)', textDecoration: 'underline' }}
                >
                  MCDefender
                </a>
                , and VR/AR projects in Unity and Unreal (see{' '}
                <a
                  href="https://github.com/clemsonwesley/Technical-Interview-Simulator-"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--accent)', textDecoration: 'underline' }}
                >
                  projects
                </a>
                ).
              </p>
              <p>
                Interned at Fidelity where I built device-lab automation and
                shipped production tooling used by QEs and engineering teams —
                a hands-on, product-focused start that shaped how I approach
                building software.
              </p>
            </div>
          </EntrySection>

          <EntrySection title="Where I'm At">
            <div className="entry-description">
              <p>
                These days I'm deep into{' '}
                <a
                  href="https://github.com/wes-fizzylabs/project_x"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--accent)', textDecoration: 'underline' }}
                >
                  exploring AI
                </a>{' '}
                — not just
                using it, but understanding the inner workings, context management, agentic workflows. I'm building with it, experimenting with
                it, and thinking about how it changes the way we
                write software. Beyond that, I'm still the same kid
                who likes poking at things — I spend time on
                vulnerability disclosure programs, always looking for
                where the cracks are. And honestly, I just love all
                things software. If it's interesting and I can learn
                something, I'm in.
              </p>
            </div>
          </EntrySection>
        </div>

        <div className="entry-footer">
          <button className="entry-button" onClick={onReturnToMap}>
            &larr; Return to Map
          </button>
          {onNext && (
            <button className="entry-button" onClick={onNext}>
              Next: {nextLabel} &rarr;
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
